(ns app.front.view.group.reporting.payments
  (:require [app.front.ui.filter-common :as filter-common]
            [app.front.view.group.admin.form-field-selection :as ff-selection]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.reporting.common :as reporting-common]
            [app.front.view.group.reporting.download-button :as rdb]
            [app.front.view.group.reporting.filters :as reporting-filters]
            [app.front.view.group.reporting.form-fields :as reporting-ff]
            [app.front.view.group.reporting.list :as report-list]
            [app.query.filter-util :as filter-util]
            [app.reporting.model :as reporting-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))


(def date-range-options
  [:reporting.date-range-options/current-month
   :reporting.date-range-options/previous-month
   :reporting.date-range-options/custom])


(def bill-states
  [:bill.state/paid
   :bill.state/unpaid])


(def default-settings
  {:report-type              #{:reporting.payment/fees-and-payments}
   :date-selector-mode       #{:reporting.date-range-options/current-month}
   :selected-optional-fields #{}
   :bill-state               #{(first bill-states)}
   :online-payment-type      #{(first reporting-model/online-payment-types)}})


(rf/reg-event-db ::set-selected
  (fn [db [_ k v]]
    (assoc-in db [::data k] v)))


(rf/reg-sub ::data :-> ::data)


(rf/reg-sub ::report-types
  :<- [::reporting-common/show-online-payments?]
  :<- [::ff/enabled? :feature-flag.id/revised-payments-report]
  :<- [::ff/enabled? :feature-flag.id/hide-deprecated-payments-report]
  (fn [[show-online-payments? revised-payments-report? hide-deprecated-payments-report?] _]
    (cond-> []
      (not hide-deprecated-payments-report?)
      (concat (when show-online-payments? [:reporting.payment/deprecated-online-payments])
              [:reporting.payment/deprecated-fees-and-payments])

      revised-payments-report?
      (concat (when show-online-payments? [:reporting.payment/online-payments])
              [:reporting.payment/fees-and-payments]))))


(rf/reg-sub ::report-type
  :<- [::data]
  :<- [::report-types]
  (fn [[data report-types] _]
    (::report-type data (first report-types))))


(rf/reg-sub ::bill-state
  :<- [::data]
  (fn [data _]
    (::bill-state data (first bill-states))))


(rf/reg-sub ::online-payment-type
  :<- [::data]
  (fn [data _]
    (::online-payment-type data (first reporting-model/online-payment-types))))


(rf/reg-sub ::report-list-report-type
  :<- [::report-type]
  :<- [::bill-state]
  :<- [::online-payment-type]
  (fn [[report-type bill-state online-payment-type] _]
    (condp = report-type
      :reporting.payment/fees-and-payments
      (case bill-state
        :bill.state/paid :reporting.payment/fees-and-payments-paid
        :bill.state/unpaid :reporting.payment/fees-and-payments-unpaid)

      :reporting.payment/deprecated-fees-and-payments
      (case bill-state
        :bill.state/paid :reporting.payment/deprecated-fees-and-payments-paid
        :bill.state/unpaid :reporting.payment/deprecated-fees-and-payments-unpaid)

      :reporting.payment/online-payments
      online-payment-type

      ;; default
      report-type)))


(rf/reg-event-fx ::select-saved-filter
  (fn [{:keys [db]} [_ selected-filter]]
    (let [filter-values       (filter-util/filter-entity->search-values-map selected-filter)
          type                (->> filter-values :report-type first)
          date-selector-mode  (->> filter-values :date-selector-mode first)
          bill-state          (->> filter-values :bill-state first)
          online-payment-type (->> filter-values :online-payment-type first)]
      {:dispatch [::reporting-ff/set-selected-from-filter filter-values]
       :db       (-> db
                     (update ::data #(p/assoc-if % ::report-type type
                                                 ::bill-state bill-state
                                                 ::online-payment-type online-payment-type))
                     (p/assoc-if ::reporting-common/date-selector-mode date-selector-mode
                                 ::reporting-common/selected-optional-fields (:optional-fields filter-values)))})))


(rf/reg-event-db ::initialize-settings
  (fn [db _]
    (let [{:keys [report-type selected-optional-fields bill-state date-selector-mode online-payment-type]} default-settings]
      (-> db
          (update ::data #(assoc % ::report-type (first report-type)
                                 ::bill-state (first bill-state)
                                 ::online-payment-type (first online-payment-type)))
          (assoc ::reporting-common/date-selector-mode (first date-selector-mode)
                 ::reporting-common/selected-optional-fields selected-optional-fields
                 ::reporting-ff/selected nil)))))


(rf/reg-sub ::filter-settings
  :<- [::gas/group]
  :<- [::report-type]
  :<- [::reporting-common/date-selector-mode]
  :<- [::bill-state]
  :<- [::online-payment-type]
  :<- [::reporting-common/selected-optional-fields]
  :<- [::reporting-ff/sorted-selected-fields]
  (fn [[group report-type date-selector-mode bill-state online-payment-type optional-fields form-fields] _]
    (util/mmap {:report-type         #{report-type}
                :date-selector-mode  #{date-selector-mode}
                :group-id            #{(:db/id group)}
                :bill-state          #{bill-state}
                :online-payment-type #{online-payment-type}}
               optional-fields
               form-fields)))


(rf/reg-sub ::filters-in-default-state?
  :<- [::filter-settings]
  (fn [filter-settings]
    (= (dissoc filter-settings :group-id) default-settings)))


(rf/reg-sub ::conditions
  :<- [::gas/group-id]
  :<- [::filter-settings]
  (fn [[group-id filter-settings]]
    (-> filter-settings
        (assoc :group-id #{group-id})
        filter-common/filter-values->conditions)))


(defn online-payments?
  [report-type]
  (= :reporting.payment/online-payments report-type))


(rf/reg-sub ::query
  :<- [::gas/group-id]
  :<- [::report-list-report-type]
  :<- [::reporting-common/date-range]
  :<- [::reporting-common/selected-optional-fields]
  :<- [::reporting-common/reporting-date-format]
  :<- [::reporting-ff/sorted-selected-fields]
  (fn [[group-id report-type [start-date end-date] optional-fields date-format form-fields] _]
    (p/assoc-if (util/mmap group-id
                           start-date
                           end-date
                           report-type
                           date-format)
                :optional-fields (some-> optional-fields seq vec)
                :form-fields (seq form-fields))))


(defn view [match]
  (let [auth-group?              (<sub [::gas/auth-group?])
        tr                       (<sub [::gas/group-tr])
        permit-types             (<sub [::gas/group-permit-types])
        report-types             (<sub [::report-types])
        report-type              (<sub [::report-type])
        bill-state               (<sub [::bill-state])
        online-payment-type      (<sub [::online-payment-type])
        query                    (<sub [::query])
        report-list-report-type  (<sub [::report-list-report-type])
        cheque-number-enabled?   (<sub [::ff/enabled? :feature-flag.id/add-cheque-number])
        revised-payments-report? (<sub [::ff/enabled? :feature-flag.id/revised-payments-report])]
    [reporting-common/report-page
     match
     tr
     [:section
      [reporting-filters/filters tr {:filter-type             :filter.type/reporting.payments
                                     :filter-settings-sub     ::filter-settings
                                     :default-filters-sub     ::filters-in-default-state?
                                     :conditions-sub          ::conditions
                                     :select-saved-filter-evt ::select-saved-filter
                                     :initialize-settings-evt ::initialize-settings}]]
     [:section
      [:h3 (tr [:reporting/select-report])]
      [reporting-common/report-type-selector
       {:tr                  tr
        :label-loc           :reporting/payment-type
        :on-change           #(>evt [::set-selected ::report-type (nth report-types %)])
        :report-type-options report-types
        :selected-index      (p/find-index report-types #(= % report-type))}]
      [:div.relative
       {:style {:padding-right "15rem"}}
       [:div.flex.flex-wrap
        [reporting-common/date-range-selector tr "payments-date-range" date-range-options]]]]

     [:section
      [reporting-common/optional-field-selector tr :payments (reporting-model/payment-optional-fields permit-types
                                                                                                      auth-group?
                                                                                                      cheque-number-enabled?)]

      (when (or auth-group?
                (ff/enabled? :feature-flag.id/superdomain-can-manage-forms))
        [:div.w-full.mt-8
         [reporting-ff/form-field-selector tr false]])]

     [:div.bx--col-xs-12.bx--col-md-6.mb-6.bx--col-xl-5
      (when (#{:reporting.payment/deprecated-fees-and-payments
               :reporting.payment/fees-and-payments} report-type)
        (into
          (cc/content-switcher
            {:selected-index (p/find-index bill-states #(= % bill-state))
             :on-change      #(>evt [::set-selected ::bill-state (->> % .-index (nth bill-states))])})
          (for [bill-state bill-states]
            (cc/switch {:text (tr [bill-state])
                        :key  (str "report-type-option-" bill-state)}))))]

     [:div.bx--col-xs-12.bx--col-md-6.mb-6.bx--col-xl-5
      (when revised-payments-report?
        (when (online-payments? report-type)
          (into
            (cc/content-switcher
              {:selected-index (p/find-index reporting-model/online-payment-types #(= % online-payment-type))
               :on-change      #(>evt [::set-selected ::online-payment-type (->> % .-index (nth reporting-model/online-payment-types))])})
            (for [online-payment-type reporting-model/online-payment-types]
              (cc/switch {:text (tr [online-payment-type])
                          :key  (str "online-payment-type" online-payment-type)})))))]

     [:section
      [:h3 (tr [:reporting/format-options])]
      [:div.flex.flex-row.flex-wrap.gap-4
       [reporting-common/date-format-selector tr]]]

     [report-list/list-section (util/mmap {:report-type report-list-report-type} tr)]
     [rdb/generate-button (util/mmap tr query)]]))


(def route
  {:name        :view/group.reporting.payments
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (let [group-id (-> params :path :group-id)]
                                  (>evt [::reporting-common/fetch-group group-id])
                                  (>evt [::reporting-common/fetch-group-configs group-id])
                                  (>evt [::reporting-common/fetch-reports group-id])
                                  (>evt [::reporting-filters/fetch-saved-filters group-id])
                                  (>evt [::reporting-common/set-date-selector-mode (first date-range-options)])
                                  (>evt [::ff-selection/fetch-form-fields group-id])))
                  :stop       (fn []
                                (>evt [::initialize-settings]))}]})
